import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EmptyState } from '../components/common/EmptyState';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { 
  Plus, Package, Briefcase, DollarSign, Clock, Edit, Trash2, Loader2,
  MoreVertical, X, Search, Sparkles, Eye, EyeOff, Grid3X3, List
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import api from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

const categories = [
  'Raw Materials', 'Components', 'Finished Goods', 'Machinery', 'Electronics',
  'Textiles', 'Food Products', 'Chemicals', 'Construction Materials',
  'Automotive Parts', 'Medical Supplies', 'Consulting', 'Logistics',
  'Marketing', 'IT Services', 'Other'
];

const priceUnits = [
  { value: 'per_unit', label: 'Per Unit' },
  { value: 'per_kg', label: 'Per Kg' },
  { value: 'per_ton', label: 'Per Ton' },
  { value: 'per_hour', label: 'Per Hour' },
  { value: 'per_project', label: 'Per Project' },
  { value: 'negotiable', label: 'Negotiable' }
];

const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'MXN', 'CNY', 'JPY'];

const emptyProduct = {
  name: '', description: '', category: '', price: '', price_unit: 'per_unit',
  currency: 'USD', min_order_quantity: '', lead_time_days: '',
  is_service: false, is_active: true
};

export default function ProductsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [errors, setErrors] = useState({});
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [drawerProduct, setDrawerProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid | list

  const { data: products, isLoading } = useQuery({
    queryKey: ['my-products'],
    queryFn: () => api.get('/profiles/products?include_inactive=true')
  });

  const createProduct = useMutation({
    mutationFn: (data) => api.post('/profiles/products', data),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['my-products']);
      toast.success('Product added');
      closeDialog();
    },
    onError: (err) => toast.error(err.message || 'Failed to add product')
  });

  const updateProduct = useMutation({
    mutationFn: ({ id, data }) => api.put(`/profiles/products/${id}`, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['my-products']);
      toast.success('Product updated');
      if (drawerProduct && drawerProduct._id === editingProduct?._id) {
        setDrawerProduct(prev => ({ ...prev, ...formData }));
      }
      closeDialog();
    },
    onError: (err) => toast.error(err.message || 'Failed to update product')
  });

  const deleteProduct = useMutation({
    mutationFn: (id) => api.delete(`/profiles/products/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries(['my-products']);
      toast.success('Product deleted');
      if (drawerProduct && drawerProduct._id === id) setDrawerProduct(null);
    },
    onError: (err) => toast.error(err.message || 'Failed to delete product')
  });

  const generateDescription = useMutation({
    mutationFn: (productName) => api.post('/ai/concierge', {
      message: `Generate a concise, professional product description (max 200 chars) for: "${productName}". Return ONLY the description text, nothing else.`,
      context: {},
      language: 'en'
    }),
    onSuccess: (data) => {
      const desc = data.message?.substring(0, 200) || '';
      setFormData(prev => ({ ...prev, description: desc }));
      toast.success('Description generated');
    },
    onError: () => toast.error('Failed to generate description')
  });

  const openDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData(product);
    } else {
      setEditingProduct(null);
      setFormData(emptyProduct);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    setFormData(emptyProduct);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) newErrors.name = 'Name is required';
    else if (formData.name.length > 50) newErrors.name = 'Name must be less than 50 characters';
    if (formData.description && formData.description.length > 200) newErrors.description = 'Description must be less than 200 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const submitData = {
      ...formData,
      price: formData.price ? parseFloat(formData.price) : null,
      min_order_quantity: formData.min_order_quantity ? parseInt(formData.min_order_quantity) : null,
      lead_time_days: formData.lead_time_days ? parseInt(formData.lead_time_days) : null
    };
    if (editingProduct) {
      updateProduct.mutate({ id: editingProduct._id, data: submitData });
    } else {
      createProduct.mutate(submitData);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatPrice = (product) => {
    if (!product.price) return t('products.priceOnRequest') || 'Price on request';
    return `${product.currency} ${product.price.toLocaleString()}/${product.price_unit?.replace('per_', '')}`;
  };

  const filteredProducts = (products || [])
    .filter(p => {
      if (filter === 'products') return !p.is_service;
      if (filter === 'services') return p.is_service;
      return true;
    })
    .filter(p => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return p.name?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q);
    });

  const productCount = products?.filter(p => !p.is_service).length || 0;
  const serviceCount = products?.filter(p => p.is_service).length || 0;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold">{t("products.productsServices")}</h1>
          <p className="text-sm text-muted-foreground">{products?.length || 0} items in catalog</p>
        </div>
        <Button onClick={() => openDialog()} data-testid="add-product-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("products.searchPlaceholder") || "Search products..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-1">
          <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>
            All ({products?.length || 0})
          </Button>
          <Button variant={filter === 'products' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('products')}>
            Products ({productCount})
          </Button>
          <Button variant={filter === 'services' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('services')}>
            Services ({serviceCount})
          </Button>
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="icon" className="h-8 w-8" onClick={() => setViewMode('grid')}>
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="icon" className="h-8 w-8" onClick={() => setViewMode('list')}>
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content area — shifts left when drawer is open */}
      <div className={`transition-all duration-200 ${drawerProduct ? 'mr-[400px]' : ''}`}>
        {isLoading ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3' : 'space-y-2'}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} className={viewMode === 'grid' ? 'h-24' : 'h-14'} />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <EmptyState
            icon={Package}
            title={search ? t('products.noMatches') || 'No matches found' : t('products.noProducts') || 'No products or services yet'}
            description={search ? 'Try a different search term.' : 'Add your products and services to include them in deals and invoices.'}
            action={!search && (
              <Button onClick={() => openDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Item
              </Button>
            )}
          />
        ) : viewMode === 'grid' ? (
          /* ========== GRID VIEW — Compact Cards ========== */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredProducts.map(product => (
              <Card
                key={product._id}
                className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/30 ${!product.is_active ? 'opacity-50' : ''} ${drawerProduct?._id === product._id ? 'border-primary shadow-md' : ''}`}
                onClick={() => setDrawerProduct(product)}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-2 mb-2">
                    {product.is_service ? (
                      <Briefcase className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                    ) : (
                      <Package className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{product.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{product.category || 'Uncategorized'}</p>
                    </div>
                    {!product.is_active && (
                      <EyeOff className="h-3 w-3 text-muted-foreground shrink-0" />
                    )}
                  </div>
                  <p className="text-xs font-medium text-primary">
                    {formatPrice(product)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* ========== LIST VIEW — Table-like rows ========== */
          <div className="border rounded-lg divide-y">
            {filteredProducts.map(product => (
              <div
                key={product._id}
                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30 ${!product.is_active ? 'opacity-50' : ''} ${drawerProduct?._id === product._id ? 'bg-muted/40' : ''}`}
                onClick={() => setDrawerProduct(product)}
              >
                {product.is_service ? (
                  <Briefcase className="h-4 w-4 text-blue-500 shrink-0" />
                ) : (
                  <Package className="h-4 w-4 text-green-500 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{product.name}</p>
                </div>
                <Badge variant="secondary" className="text-xs shrink-0 hidden sm:inline-flex">
                  {product.category || 'Uncategorized'}
                </Badge>
                <p className="text-sm text-muted-foreground shrink-0 w-32 text-right">
                  {formatPrice(product)}
                </p>
                {product.lead_time_days && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0 hidden md:flex">
                    <Clock className="h-3 w-3" />
                    {product.lead_time_days}d
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ========== DETAIL DRAWER ========== */}
      {drawerProduct && (
        <div className="fixed right-0 top-[64px] bottom-0 w-[400px] bg-background border-l shadow-xl z-30 flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
          {/* Drawer Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-2 min-w-0">
              {drawerProduct.is_service ? (
                <Briefcase className="h-5 w-5 text-blue-500 shrink-0" />
              ) : (
                <Package className="h-5 w-5 text-green-500 shrink-0" />
              )}
              <h3 className="font-semibold truncate">{drawerProduct.name}</h3>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openDialog(drawerProduct)}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setDrawerProduct(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Drawer Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Status */}
            <div className="flex items-center gap-2">
              <Badge variant={drawerProduct.is_active ? 'default' : 'secondary'}>
                {drawerProduct.is_active ? (t('products.active') || 'Active') : (t('products.inactive') || 'Inactive')}
              </Badge>
              <Badge variant="outline">
                {drawerProduct.is_service ? 'Service' : 'Product'}
              </Badge>
              {drawerProduct.category && (
                <Badge variant="secondary">{drawerProduct.category}</Badge>
              )}
            </div>

            {/* Description */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">{t("products.description")}</p>
              <p className="text-sm">{drawerProduct.description || 'No description provided.'}</p>
            </div>

            <Separator />

            {/* Pricing */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t("products.pricing")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">{t("products.price")}</p>
                  <p className="text-lg font-semibold">
                    {drawerProduct.price ? `${drawerProduct.currency} ${drawerProduct.price.toLocaleString()}` : '—'}
                  </p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">{t("products.unit")}</p>
                  <p className="text-lg font-semibold capitalize">
                    {drawerProduct.price_unit?.replace('per_', '') || '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Details */}
            {!drawerProduct.is_service && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{t("products.details")}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">{t("products.minOrderQty")}</p>
                      <p className="text-lg font-semibold">{drawerProduct.min_order_quantity || '—'}</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xs text-muted-foreground">{t("products.leadTime")}</p>
                      <p className="text-lg font-semibold">
                        {drawerProduct.lead_time_days ? `${drawerProduct.lead_time_days} days` : '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Drawer Footer */}
          <div className="p-4 border-t flex items-center gap-2">
            <Button className="flex-1" onClick={() => openDialog(drawerProduct)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="destructive"
              size="icon"
              onClick={() => {
                if (window.confirm(t('products.deleteConfirm') || 'Delete this item?')) {
                  deleteProduct.mutate(drawerProduct._id);
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ========== ADD/EDIT DIALOG ========== */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) { setEditingProduct(null); setFormData(emptyProduct); setErrors({}); }
      }}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="p-6 pb-2 flex-shrink-0">
            <DialogTitle>{editingProduct ? 'Edit Item' : 'Add New Item'}</DialogTitle>
            <DialogDescription>{formData.is_service ? (t('products.serviceDetails') || 'Service details') : (t('products.productDetails') || 'Product details')}</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-2">
            <div className="space-y-4 py-2">
              {/* Type Toggle */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  {formData.is_service ? <Briefcase className="h-5 w-5 text-blue-500" /> : <Package className="h-5 w-5 text-green-500" />}
                  <span className="font-medium">{formData.is_service ? 'Service' : 'Product'}</span>
                </div>
                <Switch checked={formData.is_service} onCheckedChange={(checked) => updateField('is_service', checked)} />
              </div>

              <div className="space-y-2">
                <Label className={errors.name ? "text-destructive" : ""}>Name <span className="text-destructive ml-1">*</span></Label>
                <Input
                  value={formData.name}
                  onChange={(e) => { updateField('name', e.target.value); if (errors.name) setErrors(prev => ({...prev, name: null})); }}
                  className={errors.name ? "border-destructive" : ""}
                  maxLength={50}
                  placeholder={formData.is_service ? (t("products.ph.service") || "Consulting Services") : (t("products.ph.product") || "Widget Pro 3000")}
                />
                {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label>{t("products.category")}</Label>
                <Select value={formData.category || ''} onValueChange={(value) => updateField('category', value)}>
                  <SelectTrigger><SelectValue placeholder={t("products.selectCategory") || "Select category"} /></SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className={errors.description ? "text-destructive" : ""}>{t("products.description")}</Label>
                  {formData.name?.trim() && (
                    <Button
                      type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1"
                      onClick={() => generateDescription.mutate(formData.name)}
                      disabled={generateDescription.isPending}
                    >
                      {generateDescription.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      AI Generate
                    </Button>
                  )}
                </div>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => { updateField('description', e.target.value); if (errors.description) setErrors(prev => ({...prev, description: null})); }}
                  className={errors.description ? "border-destructive" : ""}
                  maxLength={200}
                  placeholder={t("products.ph.describe") || "Describe your product or service..."}
                  rows={3}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  {errors.description ? <span className="text-destructive">{errors.description}</span> : <span></span>}
                  <span>{(formData.description || '').length}/200</span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>{t("products.price")}</Label>
                  <Input type="number" value={formData.price || ''} onChange={(e) => updateField('price', e.target.value)} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label>{t("products.currency")}</Label>
                  <Select value={formData.currency || 'USD'} onValueChange={(value) => updateField('currency', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{currencies.map(cur => <SelectItem key={cur} value={cur}>{cur}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("products.unit")}</Label>
                  <Select value={formData.price_unit || 'per_unit'} onValueChange={(value) => updateField('price_unit', value)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{priceUnits.map(pu => <SelectItem key={pu.value} value={pu.value}>{pu.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {!formData.is_service && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t("products.minOrderQty")}</Label>
                    <Input type="number" value={formData.min_order_quantity || ''} onChange={(e) => updateField('min_order_quantity', e.target.value)} placeholder="1" />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("products.leadTimeDays")}</Label>
                    <Input type="number" value={formData.lead_time_days || ''} onChange={(e) => updateField('lead_time_days', e.target.value)} placeholder="14" />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">{t("products.active")}</p>
                  <p className="text-sm text-muted-foreground">{t("products.showInCatalog")}</p>
                </div>
                <Switch checked={formData.is_active} onCheckedChange={(checked) => updateField('is_active', checked)} />
              </div>
            </div>
          </div>

          <div className="p-6 pt-2 flex-shrink-0 border-t bg-background">
            <DialogFooter className="pt-2 gap-2">
              <Button variant="outline" onClick={closeDialog}>{t("products.cancel")}</Button>
              <Button onClick={handleSubmit} disabled={createProduct.isPending || updateProduct.isPending}>
                {(createProduct.isPending || updateProduct.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingProduct ? (t('products.saveChanges') || 'Save Changes') : (t('products.addItem') || 'Add Item')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
